<script setup>
import { computed, defineAsyncComponent, nextTick, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import ResourceCard from '@/components/ResourceCard.vue'
import ResourceFilters from '@/components/ResourceFilters.vue'
import ResourceSearch from '@/components/ResourceSearch.vue'
import ResourceSort from '@/components/ResourceSort.vue'
import { FEATURED_ORDER } from '@/utils/resourceTags.js'
import { useCmsUiVisible } from '@/composables/useCmsUiVisible.js'
import { trackEvent } from '@/composables/useAnalytics.js'
import { isReviewLive } from '@/cms/reviewGate.js'
import { SITE_URL, withTrailingSlash } from '@/utils/siteUrl'

const CmsEditButton = import.meta.env.MODE === 'cms'
  ? defineAsyncComponent(() => import('@/cms/components/CmsEditButton.vue'))
  : null

const CmsNewContentButton = import.meta.env.MODE === 'cms'
  ? defineAsyncComponent(() => import('@/cms/components/CmsNewContentButton.vue'))
  : null

// Pre-existing gap found via the GSC trailing-slash fix's verification pass
// (2026-09-24): this page had no canonical/og:url at all, unlike every other
// production view — added here using the same baseUrl + withTrailingSlash
// pattern as usePageHead.js.
const baseUrl = import.meta.env.VITE_APP_BASE_URL || SITE_URL
const canonicalUrl = `${baseUrl}${withTrailingSlash('/resources')}`

useHead({
  title: 'Resources',
  meta: [
    { name: 'description', content: 'Browse resources from the Sylvia Rodger Institute.' },
    { property: 'og:title', content: 'Resources' },
    { property: 'og:description', content: 'Browse resources from the Sylvia Rodger Institute.' },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl }
  ],
  link: [{ rel: 'canonical', href: canonicalUrl }]
})

const route  = useRoute()
const router = useRouter()

const modules = import.meta.glob('/src/content/resources/*.md', { eager: true })

const isCmsEnabled = import.meta.env.MODE === 'cms'
const { cmsUiVisible } = useCmsUiVisible()
const showCmsChrome = computed(() => isCmsEnabled && cmsUiVisible.value)

const now = new Date()
function isPublished(publishedAt) {
  if (import.meta.env.DEV) return true
  if (!publishedAt) return true
  return new Date(publishedAt) <= now
}

const allResources = Object.entries(modules)
  .map(([path, mod]) => ({
    slug:        path.replace('/src/content/resources/', '').replace('.md', ''),
    contentPath: path,
    title:       mod.title,
    description: mod.description,
    type:        mod.type,
    topics:      mod.topics,
    externalUrl: mod.externalUrl || '',
    goInternal:  mod.goInternal || false,
    thumbnail:   mod.thumbnail,
    thumbnailContain: mod.thumbnailContain || false,
    publishedOn: mod.publishedOn || null,
    publishedAt: mod.publishedAt || null,
    reviewStatus: mod.reviewStatus || '',
  }))
  .filter(r => isPublished(r.publishedAt) && isReviewLive(r.reviewStatus))

// Newest published first — the tie-breaker under "Featured" and the whole
// order under "Published date".
function byDateDesc(a, b) {
  return new Date(b.publishedOn || b.publishedAt) - new Date(a.publishedOn || a.publishedAt)
}

// Featured rank: pinned slugs sort by their position in FEATURED_ORDER;
// everything else shares one rank just past the pins and falls back to date order.
// (Not Infinity: Infinity - Infinity is NaN, which made the comparator skip the
// date tie-break and left unpinned resources in filename order.)
function featuredRank(slug) {
  const i = FEATURED_ORDER.indexOf(slug)
  return i === -1 ? FEATURED_ORDER.length : i
}

const activeTopics = computed(() => {
  const q = route.query.topics
  return q ? q.split(',').filter(Boolean) : []
})

const activeTypes = computed(() => {
  const q = route.query.type
  return q ? q.split(',').filter(Boolean) : []
})

const query = ref(route.query.q || '')

// 'date' → Published date (newest first); anything else / absent → Featured.
const activeSort = computed(() => (route.query.sort === 'date' ? 'date' : 'featured'))

const filtered = computed(() => allResources.filter(r => {
  const topicMatch = activeTopics.value.length === 0
    || (r.topics || []).some(topic => activeTopics.value.includes(topic))
  const typeMatch = activeTypes.value.length === 0
    || activeTypes.value.includes(r.type)
  const q = query.value.trim().toLowerCase()
  const queryMatch = q === ''
    || (r.title || '').toLowerCase().includes(q)
    || (r.description || '').toLowerCase().includes(q)
  return topicMatch && typeMatch && queryMatch
}))

const sorted = computed(() => {
  const list = [...filtered.value]
  if (activeSort.value === 'date') return list.sort(byDateDesc)
  return list.sort((a, b) => {
    const rank = featuredRank(a.slug) - featuredRank(b.slug)
    return rank !== 0 ? rank : byDateDesc(a, b)
  })
})

function onUpdateSort(value) {
  const nextQuery = { ...route.query }
  if (value === 'date') nextQuery.sort = 'date'
  else delete nextQuery.sort
  router.replace({ query: nextQuery })
}

function setQuery({ topics, types }) {
  const query = { ...route.query }
  if (topics.length) query.topics = topics.join(',')
  else delete query.topics
  if (types.length) query.type = types.join(',')
  else delete query.type
  router.replace({ query })
}

function onUpdateTypes(types) {
  setQuery({ topics: activeTopics.value, types })
}

function onUpdateTopics(topics) {
  setQuery({ topics, types: activeTypes.value })
}

function clearAllFilters() {
  query.value = ''
  // Preserve the chosen sort; only clear the filter/search params.
  router.replace({ query: activeSort.value === 'date' ? { sort: 'date' } : {} })
}

const resultsSection = ref(null)

function onSearch(value) {
  query.value = value
  const nextQuery = { ...route.query }
  if (value) nextQuery.q = value
  else delete nextQuery.q
  router.replace({ query: nextQuery })
  trackEvent('search', { search_term: value })
  // Scroll position is preserved (main.js's scrollBehavior), so nudge the
  // results into view ourselves — smoothly, from wherever the page already
  // is, rather than the hard jump-to-top this used to cause.
  nextTick(() => resultsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}
</script>

<template>
  <div class="resource-centre">
    <section class="intro-band">
      <div class="container">
        <div class="header-row">
          <h1>Resources</h1>
          <CmsNewContentButton v-if="showCmsChrome" type="resources" />
        </div>
        <p class="lead">Browse evidence-based resources from the Sylvia Rodger Institute.</p>

        <ResourceSearch :modelValue="query" @search="onSearch" />
      </div>
    </section>

    <section class="content-band" ref="resultsSection">
      <div class="container">
        <div class="layout">
          <ResourceFilters
            :activeTypes="activeTypes"
            :activeTopics="activeTopics"
            @update:types="onUpdateTypes"
            @update:topics="onUpdateTopics"
            @clear="clearAllFilters"
          />

          <div class="results">
            <!-- Visually hidden: ResourceCard titles render as <h3> (matching every
                 other card usage — Home's featured strip, OurWorkDetail's "Related
                 resources"), so this page needs its own <h2> between the page <h1>
                 and the cards or heading levels jump straight from 1 to 3 (axe
                 heading-order). No other text on this page fits an on-screen h2 —
                 the count/sort row is a toolbar, not a heading — so it's sr-only. -->
            <h2 class="visually-hidden">Resource results</h2>
            <div class="results-header">
              <p class="count">
                {{ sorted.length }} {{ sorted.length === 1 ? 'resource' : 'resources' }}
              </p>
              <ResourceSort :modelValue="activeSort" @update:sort="onUpdateSort" />
            </div>

            <p v-if="sorted.length === 0" class="no-results">
              No resources match the selected filters.
              <button class="link-btn" @click="clearAllFilters">Clear filters</button>
            </p>

            <div v-else class="grid">
              <template v-for="(resource, index) in sorted" :key="resource.slug">
              <CmsEditButton v-if="showCmsChrome" :content-path="resource.contentPath" :review-status="resource.reviewStatus">
                <ResourceCard
                  :slug="resource.slug"
                  :title="resource.title"
                  :description="resource.description"
                  :type="resource.type"
                  :topics="resource.topics || []"
                  :publishedOn="resource.publishedOn || resource.publishedAt || ''"
                  :externalUrl="resource.externalUrl || ''"
                  :goInternal="resource.goInternal"
                  :thumbnail="resource.thumbnail || ''"
                  :thumbnailContain="resource.thumbnailContain"
                  :colorIndex="index"
                />
              </CmsEditButton>
              <ResourceCard
                v-else
                :slug="resource.slug"
                :title="resource.title"
                :description="resource.description"
                :type="resource.type"
                :topics="resource.topics || []"
                :publishedOn="resource.publishedOn || resource.publishedAt || ''"
                :externalUrl="resource.externalUrl || ''"
                :goInternal="resource.goInternal"
                :thumbnail="resource.thumbnail || ''"
                :thumbnailContain="resource.thumbnailContain"
                :colorIndex="index"
              />
            </template>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
// Upper band (title/intro/search) sits on the darker cream so the search
// bar reads as one distinct "start here" region; filters + results below
// sit on the standard page cream ($color-bg — matches body, set explicitly
// so the seam between bands is unambiguous).
.intro-band {
  background: var(--color-bg-alt);
  padding-block: $space-8;

  .header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-4;
    margin-bottom: $space-3;
  }

  .lead {
    font-size: $font-size-lg;
    color: var(--color-text);
    margin-bottom: $space-5;
  }
}

.content-band {
  background: var(--color-bg);
  padding-block: $space-8;

  .layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-6;

    @include from-lg {
      grid-template-columns: $sidebar-width 1fr;
      align-items: start;
    }
  }

  // Toolbar above the card grid: result count on the left, sort on the right.
  // Wraps to stacked on narrow screens.
  .results-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: $space-3;
    margin-bottom: $space-5;

    .count {
      font-size: $font-size-sm;
      color: var(--color-text-muted);
    }
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: $space-5;
  }

  .no-results {
    color: var(--color-text-muted);
    padding-block: $space-8;
  }

  .link-btn {
    background: none;
    border: none;
    color: var(--color-primary);
    cursor: pointer;
    font-family: $font-sans;
    font-size: inherit;
    padding: 0;
    margin-left: $space-2;
    &:hover         { text-decoration: underline; }
    &:focus-visible { @include focus-ring; }
    &:active        { color: var(--color-primary-press); }
  }
}
</style>
